import { datadogRum } from '@datadog/browser-rum';
import { ZonedDateTime } from '@js-joda/core';
import { CoachChat } from 'components/chat';
import Header from 'components/common/Header';
import SnackBar from 'components/common/SnackBar';
import { ClassFilterProvider } from 'contexts/classFilterContext';
import { UserProvider, useUser } from 'contexts/userContext';
import useIterable, { formatIterableDate } from 'hooks/useIterable';
import useSplit from 'hooks/useSplit';
import Page404 from 'pages/404';
import Developer from 'pages/Developer';
import DownloadApp from 'pages/DownloadApp';
import Home from 'pages/Home';
import LocationQR from 'pages/LocationQR';
import { CompleteOnMobile } from 'pages/login/CompleteOnMobile';
import FullScreen from 'pages/onDemand/FullScreen';
import RedirectQR, { QR_REDIRECT_PATH } from 'pages/RedirectQR';
import SavedClasses from 'pages/SavedClasses';
import React, { useCallback, useEffect } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import {
  createBrowserRouter,
  createRoutesFromElements,
  matchPath,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';
import { DOWNLOAD_APP_PATH } from 'utils/appStore';
import { mobileOnlyDeepLinks } from 'utils/deeplinks';
import { useMixpanel } from 'utils/mixpanel';
import packageInfo from '../../package.json';
import useLastActivity from '../hooks/useLastActivity';

const replaceModeByGenre = (search: string) => {
  const urlParamsSerialized = new URLSearchParams(search);
  const genre = urlParamsSerialized.get('mode');

  if (genre) {
    urlParamsSerialized.delete('mode');
    urlParamsSerialized.set('genre', genre);
  }

  return urlParamsSerialized.toString();
};

const replaceDeprecatedRoute = (path: string) => {
  const urlParamsSerialized = new URLSearchParams();
  const genre = path.split('/genre/')[1];
  const brand = path.split('/collection/')[1];

  if (genre) {
    urlParamsSerialized.set('genre', genre.toLowerCase());
  }
  if (brand) {
    urlParamsSerialized.set('brand', brand.toLowerCase());
  }

  return urlParamsSerialized.toString();
};

// do not show header on full screen video to avoid screen readers to detect it
const ConditionalHeader = () => {
  const location = useLocation();
  const isFullScreenRoute = !!matchPath(
    '/video/full/:wistiaId/:id',
    location.pathname,
  );
  if (isFullScreenRoute) {
    return null;
  }
  return <Header />;
};

const OndemandRedirect = () => {
  const { search } = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/',
        search: replaceModeByGenre(search),
      }}
      replace
    />
  );
};

const CollectionRedirect = () => {
  const { pathname } = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/',
        search: replaceDeprecatedRoute(pathname),
      }}
      replace
    />
  );
};

const AppWrapper = () => {
  const navigate = useNavigate();
  const { flags } = useSplit();

  useEffect(() => {
    const redirectPath = localStorage.getItem('authRedirect');

    if (redirectPath) {
      localStorage.removeItem('authRedirect');
      navigate(redirectPath);
    }
  }, [navigate]);

  return (
    <>
      <SnackBar />
      <ConditionalHeader />
      <Outlet />
      {flags['fit-web-chat'] && <CoachChat />}
    </>
  );
};

const AuthenticatedBrowserRouter = () => {
  /*
    NOTE: after add a new route, make sure to add it
    to the .pa11yci.js file to be included in the accessibility tests
  */
  const routesConfig = createRoutesFromElements(
    <Route>
      {/* Public landing page for the sign-up QR code; kept outside AppWrapper so the
          Header's onboarding redirect cannot replace it */}
      <Route path={DOWNLOAD_APP_PATH} element={<DownloadApp />} />
      <Route element={<AppWrapper />}>
        <Route path="/video/full/:wistiaId/:id" element={<FullScreen />} />
        <Route
          element={
            <ClassFilterProvider>
              <Outlet />
            </ClassFilterProvider>
          }
        >
          {/* Redirect deep link to class genres */}
          <Route path="/ondemand" element={<OndemandRedirect />} />
          <Route path="/collection">
            <Route path="*" element={<CollectionRedirect />} />
          </Route>
          <Route path="/genre">
            <Route path="*" element={<CollectionRedirect />} />
          </Route>
        </Route>
        {/* We redirect to complete on mobile if onboarding is not completed */}
        <Route
          path="/complete-on-mobile"
          element={
            <CompleteOnMobile
              showBranding={false}
              titleContent="New to Exos?"
            />
          }
        />
        <Route path="/session/:scheduleEventId" element={<Home />} />
        <Route path="/ondemand/:contentId" element={<Home />} />
        <Route path="/class/:scheduleEventId" element={<Home />} />
        <Route path="/" element={<Home />} />
        <Route path="/developer" element={<Developer />} />
        <Route path="/classes" element={<Navigate to="/" replace />} />
        <Route path="/classes/ondemand" element={<Navigate to="/" replace />} />
        <Route path="/pepsico" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/" replace />} />
        <Route path="/password-expired" element={<Navigate to="/" replace />} />
        <Route path="/callback" element={<Navigate to="/" replace />} />
        <Route
          path="/start/:email/:clientId?"
          element={<Navigate to="/" replace />}
        />
        <Route path="/saved/:contentId?" element={<SavedClasses />} />
        <Route path="/location/:locationId" element={<LocationQR />} />

        {/* Allow us to redirect any existing route to QRCode if we need to  */}
        <Route path={QR_REDIRECT_PATH}>
          <Route path="*" element={<RedirectQR />} />
        </Route>
        {/* Redirect deep links not supported to app */}
        {mobileOnlyDeepLinks.map((link) => (
          <Route path={link} key={link} element={<RedirectQR />} />
        ))}
        {/* Not Found */}
        <Route path="*" element={<Page404 />} />
        <Route path="page404" element={<Page404 />} />
      </Route>
    </Route>,
  );

  return createBrowserRouter(routesConfig);
};

const AuthenticatedLayout = () => {
  const user = useUser();
  const { iterableTrack, isIterableInitialized } = useIterable();
  const isVisible = usePageVisibility();

  const mp = useMixpanel();

  useLastActivity();

  useEffect(() => {
    datadogRum.setGlobalContextProperty('app_version', packageInfo.version);

    mp?.identify(user?.id);

    if (user?.id) {
      datadogRum.setUser({
        id: user.id,
      });
    } else {
      datadogRum.clearUser();
    }
  }, [mp, user?.id]);

  const reportOpen = useCallback(() => {
    const appOpenTime = formatIterableDate(ZonedDateTime.now());
    if (appOpenTime) {
      iterableTrack('appOpen', {
        appName: 'fit',
        platform: 'web',
        appOpenTime,
      });
    }
  }, [iterableTrack]);

  useEffect(() => {
    if (isVisible && isIterableInitialized) {
      reportOpen();
    }
  }, [isVisible, isIterableInitialized, reportOpen]);

  return <RouterProvider router={AuthenticatedBrowserRouter()} />;
};

const Authenticated = () => (
  <UserProvider>
    <AuthenticatedLayout />
  </UserProvider>
);

export default Authenticated;
